import React, { useState, useMemo, useEffect } from 'react';
import { getDaysLeft, getStatusPill, transformPlans } from '../../Library/helpers';
import { BULK_WARRANTY_EXTENSION_PRICE, BULK_WARRANTY_MID_TIER_PRICE, BULK_WARRANTY_RENEWAL_PRICE } from '../../Library/constants';
import PaymentModal from './PaymentModal';
import { ForwardArrowIcon } from '../../Assets/images/svgs';
import './style.css';

const BulkRecharge = ({ vehicles, apiData, onClose }) => {
  const [activeTab, setActiveTab] = useState('subscription');
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [paymentItem, setPaymentItem] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);

  const plans = useMemo(() => {
    const all = transformPlans(apiData?.ajjas_sub_details);
    const hwtyp = vehicles[0]?.hwtyp;
    const allSameHwtyp = vehicles.every(v => v.hwtyp === hwtyp);
    return all.filter(p => {
      if (hwtyp != null ? p._raw?.hwtyp !== hwtyp : p._raw?.hwtyp) return false;
      if (p._raw?.items && !allSameHwtyp) return false;
      return true;
    });
  }, [apiData, vehicles]);

  useEffect(() => {
    if (plans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, selectedPlanId]);

  const allWarrantiesExpired = useMemo(() => vehicles.every(v => getDaysLeft(v.warrantyExpiry) <= 0), [vehicles]);
  const allSubscriptionsExpired = useMemo(() => vehicles.every(v => getDaysLeft(v.subscriptionExpiry) <= 0), [vehicles]);

  // Warranty pricing: compute per-vehicle price, average, then snap to tier.
  const warrantyPricing = useMemo(() => {
    const extensionAmt = apiData?.ajjas_war_details?.[0]?.amt ?? BULK_WARRANTY_EXTENSION_PRICE;
    const renewalAmt = apiData?.ajjas_war_renewal?.[0]?.amt ?? BULK_WARRANTY_RENEWAL_PRICE;
    const warr_offers = apiData?.warr_offers || [];
    const perVehicle = vehicles.map(v => {
      const wDays = getDaysLeft(v.warrantyExpiry);
      const baseAmt = wDays > 0 ? extensionAmt : renewalAmt;
      const offer = warr_offers.find(o => o.did === v.did || o.warranty?.did === v.did);
      return offer?.offer?.amt ?? baseAmt;
    });
    const avg = perVehicle.reduce((s, p) => s + p, 0) / perVehicle.length;
    const tierPrice = avg < BULK_WARRANTY_MID_TIER_PRICE
      ? BULK_WARRANTY_EXTENSION_PRICE
      : avg === BULK_WARRANTY_MID_TIER_PRICE
        ? BULK_WARRANTY_MID_TIER_PRICE
        : BULK_WARRANTY_RENEWAL_PRICE;
    return { tierPrice, total: tierPrice * vehicles.length, originalTotal: renewalAmt * vehicles.length };
  }, [vehicles, apiData]);

  const selectedPlan = plans.find(p => p.id === selectedPlanId) || plans[0];
  const subPerVehicleAmt = selectedPlan?.price ?? selectedPlan?._raw?.amt ?? 0;
  const subTotal = subPerVehicleAmt * vehicles.length;
  const subOriginalTotal = undefined;

  const renderVehiclesList = (type) => (
    <div className="detail-section" style={{ marginBottom: 24 }}>
      <div className="detail-section-title">
        {type === 'subscription' ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
        )}
        Selected Vehicles ({vehicles.length})
      </div>
      <div className="info-card" style={{ padding: '0', maxHeight: '240px', overflowY: 'auto' }}>
        {vehicles.map((v, i) => {
          const days = getDaysLeft(type === 'subscription' ? v.subscriptionExpiry : v.warrantyExpiry);
          const pill = getStatusPill(days, { showBlocked: type === 'subscription' });
          return (
            <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderBottom: i === vehicles.length - 1 ? 'none' : '1px solid var(--pw-border-color)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{v.displayName}</div>
                <div style={{ fontSize: 12, color: 'var(--pw-text-secondary)' }}>{v.regNumber}</div>
              </div>
              <span className={`status-pill pill-${pill.color}`}>{pill.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="panel-main pw-page detail-page bulk-page">
      <button className="detail-back" onClick={onClose} style={{ marginLeft: 16, marginTop: 16 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        Back to Vehicles
      </button>

      <div className="detail-header" style={{ marginLeft: 16 }}>
        <h1>Bulk Recharge</h1>
        <p style={{ color: 'var(--pw-text-secondary)', fontSize: 14 }}>
          Recharge subscription or warranty for {vehicles.length} selected vehicle{vehicles.length > 1 ? 's' : ''} simultaneously.
        </p>
      </div>

      <div style={{ padding: '0 16px' }}>
        <div className="tab-bar">
          <button
            className={`tab-item ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            Subscription
          </button>
          <button
            className={`tab-item ${activeTab === 'warranty' ? 'active' : ''}`}
            onClick={() => setActiveTab('warranty')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            Extended Warranty
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'subscription' ? (
            <>
              {renderVehiclesList('subscription')}
              <div className="detail-section">
                <div className="detail-section-title">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                  Recharge Plans
                </div>

                {!allSubscriptionsExpired && (
                  <div style={{ fontSize: 13, color: 'var(--pw-color-primary)', background: 'rgba(33, 136, 255, 0.08)', padding: '10px 14px', borderRadius: 'var(--pw-radius-md)', marginBottom: 16, lineHeight: 1.4 }}>
                    💡 <strong>Good to know:</strong> Recharging early won't waste any remaining days! The new plan simply adds exactly {selectedPlan?.durationMonths === 12 ? '1 year' : selectedPlan?.durationMonths === 24 ? '2 years' : `${selectedPlan?.durationMonths ?? '—'} months`} to each vehicle's current expiry date.
                  </div>
                )}

                <div className="plans-grid">
                  {plans.map(plan => (
                    <div
                      key={plan.id}
                      className={`plan-card ${plan.isPopular ? 'popular' : ''} ${selectedPlanId === plan.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPlanId(plan.id)}
                    >
                      {plan.isPopular && (
                        <div className="plan-badge">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                          Most Popular
                        </div>
                      )}
                      <div className="plan-title">{plan.title}</div>
                      <div className="plan-duration">{plan.duration}</div>
                      <div>
                        <span className="plan-price">₹{Math.round(plan.price / 1.18)}</span>
                        <span className="plan-gst-tag">+ GST</span>
                        {plan.originalPrice && <span className="plan-original-price">₹{plan.originalPrice}</span>}
                      </div>
                      {plan.savings && <div className="plan-savings">{plan.savings}</div>}
                      <ul className="plan-features">
                        {plan.features.map((f, i) => (
                          <li key={i}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pw-color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                            {f}
                          </li>
                        ))}
                      </ul>
                      {plan.subtext && <div className="plan-subtext">{plan.subtext}</div>}
                    </div>
                  ))}
                </div>

                <div className="sticky-cta">
                  <div className="sticky-cta-info" style={{ minWidth: 0, flex: 1 }}>
                    <div className="sticky-label">Selected Plan</div>
                    <div className="sticky-value" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedPlan?.title} — ₹{subTotal} · {vehicles.length} vehicle{vehicles.length > 1 ? 's' : ''}
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ flexShrink: 0, marginLeft: 16 }}
                    onClick={() => selectedPlan && setPaymentItem({
                      title: selectedPlan.title,
                      price: subTotal,
                      originalPrice: subOriginalTotal,
                      type: 'Subscription',
                      planIdx: selectedPlan.idx,
                      subs: vehicles[0]._sub,
                      quantity: vehicles.length,
                      bulk: true,
                      allSubs: vehicles.map(v => ({ sid: v._sub?.idx })),
                    })}
                  >
                    Continue
                    <ForwardArrowIcon width={14} height={14} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {renderVehiclesList('warranty')}
              <div className="detail-section">
                <div className="detail-section-title">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                  Extend Warranty
                </div>

                {!allWarrantiesExpired && (
                  <div style={{ fontSize: 13, color: 'var(--pw-color-primary)', background: 'rgba(33, 136, 255, 0.08)', padding: '10px 14px', borderRadius: 'var(--pw-radius-md)', marginBottom: 16, lineHeight: 1.4 }}>
                    💡 <strong>Good to know:</strong> Extending early won't waste any remaining days! The new plan simply adds exactly 1 year to each vehicle's current expiry date.
                  </div>
                )}

                <div className="warranty-grid">
                  <div className="warranty-pricing-card">
                    <div style={{ marginBottom: 6, fontSize: 13, color: 'var(--pw-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      1 Year Extended Warranty
                    </div>
                    <div>
                      <span className="warranty-price-big">₹{warrantyPricing.total}</span>
                      {warrantyPricing.total < warrantyPricing.originalTotal && (
                        <span className="warranty-price-original">₹{warrantyPricing.originalTotal}</span>
                      )}
                    </div>
                    {warrantyPricing.total < warrantyPricing.originalTotal && (
                      <div className="warranty-tag green">Save ₹{warrantyPricing.originalTotal - warrantyPricing.total}</div>
                    )}
                    <button
                      className="warranty-cta-btn"
                      onClick={() => setPaymentItem({
                        title: 'Extended Warranty',
                        price: warrantyPricing.total,
                        originalPrice: warrantyPricing.total < warrantyPricing.originalTotal ? warrantyPricing.originalTotal : undefined,
                        type: 'Warranty',
                        subs: vehicles[0]._warranty,
                        quantity: vehicles.length,
                        bulk: true,
                        allSubs: vehicles.map(v => ({ sid: v._warranty?.idx })),
                      })}
                    >
                      Extend Warranty @ ₹{warrantyPricing.total}
                      {warrantyPricing.total < warrantyPricing.originalTotal && (
                        <span style={{ textDecoration: 'line-through', opacity: 0.6, marginLeft: 8, fontWeight: 400 }}>₹{warrantyPricing.originalTotal}</span>
                      )}
                    </button>
                  </div>

                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Why Extend Warranty?</div>
                    <ul className="warranty-benefits">
                      {[
                        { title: 'Guaranteed Replacement', desc: 'Free replacement in case of device failure', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg> },
                        { title: 'Save up to ₹1200', desc: 'On repair and servicing costs per vehicle', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg> },
                        { title: 'Instant Support', desc: 'Priority customer support access', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.9 19.79 19.79 0 0 1 1.61 3.28 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg> },
                        { title: 'Free Extended Subscription', desc: 'No charges during device downtime', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg> },
                      ].map(({ title, desc, Icon }) => (
                        <li key={title} className="warranty-benefit-item">
                          <div className="warranty-benefit-icon"><Icon /></div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{title}</div>
                            <div style={{ fontSize: 12, color: 'var(--pw-text-secondary)', marginTop: 2 }}>{desc}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {paymentItem && (
        <PaymentModal
          item={paymentItem}
          onClose={() => {
            setPaymentItem(null);
            if (paymentSucceeded) {
              onClose();
            }
          }}
          onSuccess={() => setPaymentSucceeded(true)}
        />
      )}
    </div>
  );
};

export default BulkRecharge;
